import React, { useEffect, useRef, useState } from "react";
import BasicHeader from '../../components/basicHeader/BasicHeader';
import Footer from "../../components/adminFooter/Footer.js";
import "./rtrconfirmation.css";
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import WorkIcon from "../../resources/images/workIcon.svg";
import CancelIcon from '@mui/icons-material/Cancel';
import { NotificationManager } from "react-notifications";
import { useSearchParams } from "react-router-dom";
import getCandidateRequisitionInfo from "../../apis/getCandidateRequisitionInfo/getCandidateRequisitionInfo";
import updateCandidateRequisitionAttachment from "../../apis/updateCandidateRequisitionAttachment/updateCandidateRequisitionAttachment";
import { generateRTRDocument, uploadRTRToBlobStorage, convertTextToSignatureImage } from "./RTRassistingFunctions";
import Decline from "../../components/Icons/decline";


function RtrConfirmation() {
  const [searchParams] = useSearchParams();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [candidateRequisitionData, setCandidateRequisitionData] = useState({});
  const [candidateConsentData, setCandidateConsentData] = useState({
    candidateRequisitionId: searchParams.get("candidateReqId"),
    action: searchParams.get("action"),
    signatureFile: null,
    signaturePreview: null,
    loadingSignature: false,
    candidateSignatureText: "",
    textSignaturePreview: null,
    loadingTextSignature: false,
    aggreeToTerms: false,
    isDeclined: false,
    isApproved: false,
    statusMessage: "",
    isCurrentStatus: false,
    declineApiRunning: false,
    isDeclinedDialogOpen: false,
    applyApiRunning: false,
  });
  const fileInputRef = useRef();
  const hasFetched = useRef(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        NotificationManager.error("Please select an image file.", "Error", 10000);
        e.target.value = "";
        return;
      }
      if (file.size > 50 * 1024) {
        NotificationManager.error("File size must be less than 50KB.", "Error", 10000);
        e.target.value = "";
        return;
      }
      setCandidateConsentData({
        ...candidateConsentData,
        signatureFile: file,
        loadingSignature: true,
        signaturePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleSignatureTextChange = async (e) => {
    const text = e.target.value;
    setCandidateConsentData({
      ...candidateConsentData,
      candidateSignatureText: text,
      loadingTextSignature: text.length > 0,
    });

    // Generate preview for text signature
    if (text.trim().length > 0) {
      try {
        const textSignatureImage = await convertTextToSignatureImage(text, {
          fontSize: 28,
          fontFamily: "Segoe Script, cursive",
          color: "#1e3a8a",
          minWidth: 80,
          maxWidth: 500,
          height: 70,
          padding: 20
        });

        setCandidateConsentData(prev => ({
          ...prev,
          candidateSignatureText: text,
          textSignaturePreview: textSignatureImage.dataUrl,
          loadingTextSignature: false,
        }));
      } catch (error) {
        console.error("Error generating text signature preview:", error);
        setCandidateConsentData(prev => ({
          ...prev,
          candidateSignatureText: text,
          textSignaturePreview: null,
          loadingTextSignature: false,
        }));
      }
    } else {
      setCandidateConsentData(prev => ({
        ...prev,
        candidateSignatureText: text,
        textSignaturePreview: null,
        loadingTextSignature: false,
      }));
    }
  };

  const handleAcceptCheckbox = (e) => {
    setCandidateConsentData({
      ...candidateConsentData,
      aggreeToTerms: e.target.checked,
    });
  };

  const handleClear = () => {
    setCandidateConsentData({
      ...candidateConsentData,
      signatureFile: null,
      signaturePreview: null,
      loadingSignature: false,
    });
    fileInputRef.current.value = "";
  };

  const handleClearTextSignature = () => {
    setCandidateConsentData({
      ...candidateConsentData,
      candidateSignatureText: "",
      textSignaturePreview: null,
      loadingTextSignature: false,
    });
  };

  const handleApply = async () => {
    if (!candidateConsentData.aggreeToTerms) {
      NotificationManager.error(
        "Please agree to the terms before applying.",
        "Error",
        10000
      );
      return;
    }
    if (
      !candidateConsentData.signatureFile &&
      !candidateConsentData.candidateSignatureText
    ) {
      NotificationManager.error(
        "Please provide a signature before applying.",
        "Error",
        10000
      );
      return;
    }

    // Set loading state
    setCandidateConsentData({
      ...candidateConsentData,
      applyApiRunning: true,
    });

    try {
      let generatedFile = await generateRTRDocument(candidateRequisitionData, candidateConsentData);

      const fileUrl = await uploadRTRToBlobStorage({
        file: generatedFile,
        candidateRequisitionId: candidateConsentData.candidateRequisitionId,
      });

      if (!fileUrl) {
        NotificationManager.error("RTR upload failed !!", "error", 10000);
        setCandidateConsentData({
          ...candidateConsentData,
          applyApiRunning: false,
        });
        return;
      }

      const payload = {
        candidateRequisitionId: candidateConsentData.candidateRequisitionId,
        rtr: [
          {
            link: fileUrl,
            updatedBy: "system",
          },
        ],
        approvalStatus: {
          isPending: false,
          isApproved: true,
          isRejected: false,
        },
      };

      const response = await updateCandidateRequisitionAttachment(payload);

      if (response && response.status === 200) {
        setCandidateConsentData({
          ...candidateConsentData,
          isApproved: true,
          applyApiRunning: false,
        });
        NotificationManager.success(
          "RTR for this application submitted successfully.",
          "Success",
          10000
        );
      } else {
        setCandidateConsentData({
          ...candidateConsentData,
          applyApiRunning: false,
        });
        NotificationManager.error("Error while submitting RTR for this application!", "error", 10000);
      }
    } catch (error) {
      console.error(
        "Error while submitting RTR for this application",
        error
      );
      setCandidateConsentData({
        ...candidateConsentData,
        applyApiRunning: false,
      });
      NotificationManager.error("Error while submitting RTR for this application!", "error", 10000);
    }
  };

  const handleDecline = async () => {
    setCandidateConsentData({
      ...candidateConsentData,
      declineApiRunning: true,
    });

    const payload = {
      candidateRequisitionId: candidateConsentData.candidateRequisitionId,
      rtr: [],
      approvalStatus: {
        isPending: false,
        isApproved: false,
        isRejected: true,
      },
    };
    const response = await updateCandidateRequisitionAttachment(payload);
    if (response && response.status === 200) {
      setCandidateConsentData({
        ...candidateConsentData,
        isDeclined: true,
        isDeclinedDialogOpen: false,
        declineApiRunning: false,
      });
      setIsDataLoading(false);
      NotificationManager.success(
        "Application declined successfully.",
        "Success",
        10000
      );
    } else {
      NotificationManager.error(
        "Unable to decline the application. Please try again later.",
        "Error",
        10000
      );
    }
  }

  const handleDeclineCancel = () => {
    setIsDataLoading(false);
    setCandidateConsentData({
      ...candidateConsentData,
      isDeclined: false,
      isDeclinedDialogOpen: false,
    });
  }

  const fetchCandidateRequisitionData = async () => {
    try {
      let response = await getCandidateRequisitionInfo({
        candidateRequisitionId: candidateConsentData.candidateRequisitionId,
      });

      if (response && response.status === 200) {
        let candidateRequisitionDataObj = {
          ...response.data.data[0].candidateRequisition,
          recruiterData: response.data.data[0].recruiterData || {},
        }
        setCandidateRequisitionData(candidateRequisitionDataObj);
        let responseData = response.data.data[0].candidateRequisition;

        let declineDialogOpen = false
        let dataLoadingStatus = true;


        if (responseData?.candidateRequisitionAttachments?.approvalStatus?.isPending) {
          if (candidateConsentData.action === "accept" && candidateConsentData.candidateRequisitionId) {
            dataLoadingStatus = false;
          }
          else if (candidateConsentData.action === "decline" && candidateConsentData.candidateRequisitionId) {
            declineDialogOpen = true
          }
        }
        else {
          dataLoadingStatus = false;
        }
        setCandidateConsentData({
          ...candidateConsentData,
          isDeclined: responseData?.candidateRequisitionAttachments?.approvalStatus?.isRejected || false,
          isApproved: responseData?.candidateRequisitionAttachments?.approvalStatus?.isApproved || false,
          isCurrentStatus: responseData?.candidateRequisitionAttachments?.approvalStatus?.isPending ? false : true,
          isDeclinedDialogOpen: declineDialogOpen,
        })
        setIsDataLoading(dataLoadingStatus);
      }
    } catch (error) {
      console.error("Error fetching candidate requisition data:", error);
      NotificationManager.error(
        "Unable to retrieve required information. Please try again later.",
        "Error",
        10000
      );
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchCandidateRequisitionData();
      hasFetched.current = true;
    }
  }, []);

  return (
    <>
      <div className="mainContainer_rtr">
        <div>
          <BasicHeader />

          {isDataLoading ? (
            <div className="circularProgressContainer">
              <CircularProgress />
            </div>
          ) : (

            candidateConsentData.isDeclined || candidateConsentData.isApproved ? <>

              <div className="statusMessageContainer">
                {candidateConsentData.isApproved ? <>
                  <div className="statusIcon approved">
                    ✓
                  </div>
                  <h2 className="statusTitle approved">
                    {candidateConsentData.isCurrentStatus ?
                      "Application Already Submitted" :
                      "Application Submitted Successfully!"}
                  </h2>
                  {candidateConsentData.isCurrentStatus ?
                    <p className="statusMessage">
                      You have already confirmed your Right to Represent (RTR) acknowledgment for this position.
                      Your application was successfully submitted to our recruitment team.
                    </p>
                    :
                    <p className="statusMessage">
                      Thank you for confirming your Right to Represent (RTR) acknowledgment.
                      Your application has been successfully submitted to our recruitment team.
                    </p>
                  }

                </> : <>
                  <div className="statusIcon declined">
                    ✕
                  </div>
                  <h2 className="statusTitle declined">
                    {candidateConsentData.isCurrentStatus ?
                      "Application Already Withdrawn" :
                      "Application Withdrawn Successfully!"
                    }
                  </h2>
                  {candidateConsentData.isCurrentStatus ?
                    <p className="statusMessage">
                      You have already declined your Right to Represent (RTR) acknowledgment for this position.
                      Your application withdrawal has been processed.
                    </p>
                    :
                    <p className="statusMessage">
                      You have successfully withdrawn your application for this position.
                      Your Right to Represent (RTR) acknowledgment has been declined as requested.
                    </p>
                  }
                </>}


              </div>

            </> :

              <Grid container className="contentContainer_rtr">
                <Grid container size={{ xs: 12 }} >
                  <Grid size={{ xs: 12 }} className="candidateTitle">
                    <span>Hi {candidateRequisitionData?.name || "N/A"} ,</span>
                  </Grid>
                  <Grid size={{ xs: 12 }} className="candidateConfirmationTitle">
                    <div>
                      We just need to confirm your application for the{" "}
                      <span className="highlightedText">
                        {candidateRequisitionData?.requisitionDetail
                          ?.primarySkill || "N/A"}
                      </span>{" "}
                      at{" "}
                      <span className="highlightedText">
                        {candidateRequisitionData?.clientDetails?.clientName ||
                          "N/A"}
                      </span>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <div className="workinfoContainerGrid">
                      <div className="workIconContainer">
                        <img src={WorkIcon} alt="work icon" className="workIcon" />
                      </div>

                      <div className="potitionInfoContainer">
                        <span>
                          {candidateRequisitionData?.requisitionDetail
                            ?.primarySkill || "N/A"}
                        </span>
                        <span className="locationContainer">
                          {candidateRequisitionData?.requisitionDetail
                            ?.location[0] || "N/A"}
                        </span>
                      </div>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12 }} className="confirmationTagline">
                    <span>
                      By signing this document, I agree to apply for this position.
                    </span>
                  </Grid>

                  <Grid size={{ xs: 12 }} className="typeSignatureFieldContainer">
                    <input
                      type="text"
                      placeholder="Type your full name"
                      className="typeSignatureField"
                      value={candidateConsentData.candidateSignatureText}
                      onChange={(e) => handleSignatureTextChange(e)}
                    />
                    {candidateConsentData.textSignaturePreview && (
                      <div className="textSignaturePreview">
                        {candidateConsentData.loadingTextSignature && (
                          <div className="signatureLoader"></div>
                        )}
                        <img
                          src={candidateConsentData.textSignaturePreview}
                          alt="Text Signature Preview"
                          className="textSignatureImage"
                          style={{
                            display: candidateConsentData.loadingTextSignature
                              ? "none"
                              : "block",
                          }}
                        />
                        {!candidateConsentData.loadingTextSignature && (
                          <span
                            className="signatureFileClear"
                            onClick={handleClearTextSignature}
                            title="Clear"
                          >
                            <CancelIcon />
                          </span>
                        )}
                      </div>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12 }} className="orTextContainer">
                    <span>or</span>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <div className="uploadSignatureContainer">
                      <button
                        className="uploadSignatureButton"
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                      >
                        {candidateConsentData?.signatureFile
                          ? "Re-upload Signature"
                          : "Upload Signature"}
                      </button>
                      <input
                        ref={fileInputRef}
                        className="signatureFileInput"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      {candidateConsentData?.signaturePreview && (
                        <div className="signatureImagePreview">
                          {candidateConsentData?.loadingSignature && (
                            <div className="signatureLoader"></div>
                          )}
                          <img
                            src={candidateConsentData?.signaturePreview}
                            alt="Signature Preview"
                            className="signatureImage"
                            style={{
                              display: candidateConsentData?.loadingSignature
                                ? "none"
                                : "block",
                            }}
                            onLoad={() =>
                              setCandidateConsentData({
                                ...candidateConsentData,
                                loadingSignature: false,
                              })
                            }
                          />
                          {!candidateConsentData.loadingSignature && (
                            <span
                              className="signatureFileClear"
                              onClick={handleClear}
                              title="Clear"
                            >
                              <CancelIcon />
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12 }} className="confirmationCheckboxContainer">
                    <input
                      type="checkbox"
                      className="confirmationCheckbox"
                      checked={candidateConsentData?.aggreeToTerms}
                      onChange={(e) => handleAcceptCheckbox(e)}
                    />
                    <span>
                      By checking this box, I agree electronically sign and submit
                      this application
                    </span>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <button
                      className={candidateConsentData.applyApiRunning ? "applyButton_loading" : "applyButton"}
                      onClick={handleApply}
                      disabled={candidateConsentData.applyApiRunning}
                    >
                      {candidateConsentData.applyApiRunning && (
                        <CircularProgress size={16} style={{ marginRight: 8, color: 'white' }} />
                      )}
                      <span>{candidateConsentData.applyApiRunning ? "Applying..." : "Apply"}</span>
                    </button>
                  </Grid>
                </Grid>
              </Grid>
          )}
        </div>
        <Footer />
      </div>
      <Dialog
        open={candidateConsentData.isDeclinedDialogOpen}
        onClose={() => handleDeclineCancel()}
        aria-labelledby="customized-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent className="dialogContent_rtrDecline">
          <div className="dialogTitleContainer_rtrDecline">
            <Decline />
            <span>Confirm decline application</span>
          </div>

          <div className="dialogDescription_rtrDecline">
            Are you sure you want to decline the application for the position{" "}
            <span className="highlightedText">
              {candidateRequisitionData?.requisitionDetail?.primarySkill || "N/A"}
            </span>{" "}
            at{" "}
            <span className="highlightedText">
              {candidateRequisitionData?.clientDetails?.clientName || "N/A"}
            </span>?
            This action cannot be undone.
          </div>
        </DialogContent>
        <DialogActions className="dialogActions_rtrDecline">
          <button className="cancelButton_rtrDecline" onClick={() => handleDeclineCancel()} disabled={candidateConsentData.declineApiRunning}>
            <span>No</span>
          </button>
          <button className={candidateConsentData.declineApiRunning ? "confirmButton_rtrDecline_loading" : "confirmButton_rtrDecline"} onClick={(e) => handleDecline()}>
            {candidateConsentData.declineApiRunning && (
              <CircularProgress />
            )}
            <span>Yes</span>
          </button>
        </DialogActions>

      </Dialog>

    </>
  );
}

export default RtrConfirmation;
